import { useState } from "react";

import {
  AppDialog,
  AppDialogActions,
  DialogPrimaryButton,
  DialogSecondaryButton,
} from "@/components/ui/app-dialog";
import {
  CommunityIdAutocomplete,
  type CommunityIdChange,
} from "@/components/ui/community-id-autocomplete";
import { focusPaperThenFirstInput } from "@/components/ui/dialog-focus";
import { ErrorSnackbar } from "@/components/ui/error-snackbar";
import { MuiLink } from "@/components/ui/mui-link";
import { MuiTypography } from "@/components/ui/mui-typography";
import { RichTextField } from "@/components/ui/rich-text-field";
import { TextField } from "@/components/ui/text-field";
import type { SpecType } from "@/api/contracts/specifications";
import type { SpecListRow } from "@/hooks/specifications";
import { specDetailPath } from "@/lib/route-paths";
import { SPEC_LIFECYCLE_COPY as COPY } from "./spec-lifecycle-copy";

// The Spec list's rename dialogs — one per donor, chosen by the row's type as
// the donor's ⋮ menu chooses (`spec-actions-button.tsx:57-61`):
//
//   - a core or base row opens `EditSpecDialog` (`spec-actions-button/
//     edit-spec-dialog.tsx`): "Edit Specification Name", one field;
//   - a community row opens the community island's EDIT variant
//     (`CommunitySpecCreateEditDupIsland/components/
//     community-spec-create-edit-delete-dialog.tsx`): "Edit Specification Name
//     & Community ID", the "Based on:" line, the name and the Communities
//     field.
//
// Presentational, as the create dialog is: the host owns the mutation and
// mounts a fresh form per open, which is the donor's reset on every open.
//
// **The Editor header pencil's two** (#806, `EditSpecHeaderDialog` below) are
// the same two donors in their notes-carrying form — `SpecificationFormDialog`
// in `edit` mode for core and base, and the community island's
// `edit-with-notes` variant — built from the same pieces over the spec
// summary's fields rather than a list row.
//
// What the two share is the donor's, not a merge: Save reads "Save" / "Saving"
// under the one accessible name "Save Edits"; Cancel is never held; the name
// is held while the write is in flight (react-hook-form's `isSubmitting` in
// its `InputField`) — the Community ID, cloned in with the form alone, is not;
// a failure keeps the dialog open under its own error string; Escape, the
// overlay and the X close it whatever is in flight; and each opens on its
// paper and focuses the name once it has entered (`focusFirstInput` on
// `onEntered` — the community donor's `autoFocus` lands on `InputField`'s
// `FormControl` div, where it does nothing). Where they differ is where the donors differ —
// the Save rule, the layout, and when the name's rule is seen.

export interface RenameInput {
  name: string;
  /** A community row's id, which its request always carries. */
  communityId?: string;
}

export interface EditSpecNameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  row: SpecListRow;
  pending: boolean;
  /** The last rename failed: the error snackbar is up. */
  failed: boolean;
  onErrorClose: () => void;
  onSubmit: (input: RenameInput) => void;
  /** Where the focus goes on closing — see `AppDialog`. */
  onCloseAutoFocus?: (event: Event) => void;
}

export function EditSpecNameDialog(props: EditSpecNameDialogProps) {
  return props.row.type === "community" ? (
    <CommunitySpecFormDialog
      {...props}
      copy={COPY.rename.community}
      submitCopy={COPY.rename.save}
      storedName={props.row.name}
      storedCommunityId={props.row.communityId ?? ""}
      parent={props.row.parentSpec}
      onSubmit={({ name, communityId }) => props.onSubmit({ name, communityId })}
    />
  ) : (
    <EditPlainSpecDialog {...props} />
  );
}

/** A community spec's Parent version, as its Based on line links it. */
export type SpecParentLink = SpecListRow["parentSpec"];

/** The summary's fields the header pencil's dialogs read. */
export interface SpecHeaderEditFields {
  name: string;
  type: SpecType;
  communityId: string | null;
  /** HTML, as the notes block renders it. */
  notes: string | null;
  /** The community spec's Parent version, for its Based on line. */
  parent: SpecParentLink;
}

/** The header write's fields, shaped as `update-spec-summary-action.ts` sends them. */
export interface SpecHeaderEditInput {
  name: string;
  notes: string;
  /** A community spec's — never sent for core or base. */
  communityId?: string;
}

/**
 * The donor's shaping (`update-spec-summary-action.ts:15-26`), done here
 * because the server stores what it receives (#799 Assumption 8): the name
 * trimmed — a blank one leaves the stored name, which is what the donor's
 * omitted field did — and the notes trimmed, `""` when empty, always sent.
 * The Community ID only on a community spec: the donor's core/base form sends
 * the text "undefined" there (`EditSpecSummary.tsx:27,56`), a defect not
 * ported (#806 enumeration D7).
 */
function headerEditInput(
  spec: SpecHeaderEditFields,
  values: { name: string; notes: string; communityId?: string },
): SpecHeaderEditInput {
  const name = values.name.trim() || spec.name;
  const notes = values.notes.trim();
  return spec.type === "community"
    ? { name, communityId: values.communityId ?? "", notes }
    : { name, notes };
}

export interface EditSpecHeaderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  spec: SpecHeaderEditFields;
  pending: boolean;
  /** The last write failed: the community dialog's error snackbar is up. */
  failed: boolean;
  onErrorClose: () => void;
  onSubmit: (input: SpecHeaderEditInput) => void;
}

/**
 * The header pencil's dialog for the spec's type (`EditSpecSummary.tsx:85-93`):
 * `Edit` on core and base, `Edit Specification Name & Community ID` with the
 * notes on community.
 *
 * Mount it once and leave it mounted: the core/base form keeps what was typed
 * across a close until the stored values change, as the donor's `useForm`
 * does. The community form resets on every opening, as the island's does
 * (`observers.ts:78-84`) — this component remounts it per opening itself.
 */
export function EditSpecHeaderDialog(props: EditSpecHeaderDialogProps) {
  // Each opening of the community form is a fresh form.
  const [openings, setOpenings] = useState(props.open ? 1 : 0);
  const [wasOpen, setWasOpen] = useState(props.open);
  if (wasOpen !== props.open) {
    setWasOpen(props.open);
    if (props.open) setOpenings((count) => count + 1);
  }
  const { spec } = props;
  if (spec.type !== "community") return <EditSpecHeaderPlainDialog {...props} />;
  return (
    <CommunitySpecFormDialog
      key={openings}
      copy={COPY.rename.community}
      submitCopy={COPY.rename.save}
      open={props.open}
      onOpenChange={props.onOpenChange}
      pending={props.pending}
      failed={props.failed}
      onErrorClose={props.onErrorClose}
      storedName={spec.name}
      storedCommunityId={spec.communityId ?? ""}
      parent={spec.parent}
      notes={{ stored: spec.notes ?? "" }}
      onSubmit={(values) => props.onSubmit(headerEditInput(spec, values))}
    />
  );
}

const HEADER_PLAIN_FORM_ID = "spec-form-dialog";

/**
 * `Edit` — `SpecificationFormDialog` in `edit` mode (`:25-116`): `sm`,
 * `fullWidth`, no subtitle; a `Grid container spacing={2}` of the name and the
 * rich-text notes; Cancel, then Save — held while saving or once the name is
 * empty (untrimmed, as the donor's `!name`). No `Saving` text and no spinner:
 * the donor's button has neither. Closing on a failure is the host's, as the
 * donor's unawaited trigger closes it (#806 Assumption 3).
 */
function EditSpecHeaderPlainDialog({
  open,
  onOpenChange,
  spec,
  pending,
  onSubmit,
}: EditSpecHeaderDialogProps) {
  const stored = { name: spec.name, notes: spec.notes ?? "" };
  const [values, setValues] = useState(stored);
  // The donor's `useEffect(reset, [name, communityId, notes])`: re-seeded when
  // the stored values change, kept across a close otherwise.
  const storedKey = `${stored.name}\u0000${stored.notes}`;
  const [seededKey, setSeededKey] = useState(storedKey);
  if (seededKey !== storedKey) {
    setSeededKey(storedKey);
    setValues(stored);
  }
  const blocked = pending || !values.name;

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={COPY.header.plainTitle}
      size="sm"
      onOpenAutoFocus={focusPaperThenFirstInput}
      actions={
        <AppDialogActions>
          <DialogSecondaryButton onClick={() => onOpenChange(false)}>
            {COPY.cancel}
          </DialogSecondaryButton>
          <DialogPrimaryButton type="submit" form={HEADER_PLAIN_FORM_ID} disabled={blocked}>
            {COPY.header.save}
          </DialogPrimaryButton>
        </AppDialogActions>
      }
    >
      <form
        id={HEADER_PLAIN_FORM_ID}
        data-testid="specification-form-dialog"
        noValidate
        autoComplete="off"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (blocked) return;
          onSubmit(headerEditInput(spec, values));
        }}
      >
        <TextField
          label={COPY.nameLabel}
          required
          value={values.name}
          onChange={(name) => setValues((current) => ({ ...current, name }))}
          disabled={pending}
          className="w-full"
        />
        <RichTextField
          label={COPY.header.notesLabel}
          placeholder={COPY.header.notesPlaceholder}
          value={values.notes}
          onChange={(notes) => setValues((current) => ({ ...current, notes }))}
          disabled={pending}
        />
      </form>
    </AppDialog>
  );
}

const PLAIN_FORM_ID = "edit-spec-form-dialog";
const COMMUNITY_FORM_ID = "edit-community-spec-form-dialog";

/**
 * Core and base. `sm`, `fullWidth`; the form is a one-cell grid carrying `py:
 * 3` — 24px above and below the field, inside the content's own padding
 * (`edit-spec-dialog.tsx:106-115`). Save is held on `!name || name ===
 * current`, so the field's "Required field" can never be seen.
 */
function EditPlainSpecDialog({
  open,
  onOpenChange,
  row,
  pending,
  failed,
  onErrorClose,
  onSubmit,
  onCloseAutoFocus,
}: EditSpecNameDialogProps) {
  const [name, setName] = useState(row.name);
  const blocked = !name || name === row.name;

  return (
    <FormDialogFrame
      open={open}
      onOpenChange={onOpenChange}
      copy={COPY.rename.plain}
      submitCopy={COPY.rename.save}
      formId={PLAIN_FORM_ID}
      pending={pending}
      blocked={blocked}
      failed={failed}
      onErrorClose={onErrorClose}
      onCloseAutoFocus={onCloseAutoFocus}
    >
      <form
        id={PLAIN_FORM_ID}
        noValidate
        autoComplete="off"
        className="py-6"
        onSubmit={(event) => {
          event.preventDefault();
          if (blocked || pending) return;
          onSubmit({ name });
        }}
      >
        <TextField
          label={COPY.nameLabel}
          required
          value={name}
          onChange={setName}
          disabled={pending}
          className="w-full"
        />
      </form>
    </FormDialogFrame>
  );
}

/**
 * The community island's form (`community-spec-create-edit-delete-dialog.tsx`),
 * in every variant but create-from-community: its `edit` and `edit-with-notes`
 * here, and the cloner's `duplicate` and `create` (#812,
 * `community-spec-dialogs.tsx`), which hand in their own copy, their opening
 * values and — `create` only — the locked Division.
 *
 * Submit is held **only** while both the name and the id equal the stored
 * ones (`:195-197`) — the original's for edit and duplicate, `''` for create;
 * the rest is react-hook-form's submit: an empty name reads "Required field" and, from
 * then on, re-validates as it is typed (`reValidateMode: 'onChange'`), while
 * the Communities field's own rule message is handed back at submit and holds
 * until the next one — the donor's field writes through `setValue`, which never
 * re-validates.
 */
export interface CommunitySpecFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  failed: boolean;
  onErrorClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
  /** The variant's title, subtitle and refusal string. */
  copy: { title: string; subtitle: string; error: string };
  /** The variant's submit button: its label, its pending label and its name. */
  submitCopy: { label: string; pending: string; ariaLabel: string };
  /** What the submit rule compares against — `''` for a create. */
  storedName: string;
  storedCommunityId: string;
  /** The name the form opens on; the stored one unless given. */
  initialName?: string;
  /** The Community ID the field opens on; the stored one unless given. */
  initialCommunityId?: string;
  parent: SpecParentLink;
  /** `create`'s locked Division, between the name and the Community ID. */
  division?: React.ReactNode;
  /** The `edit-with-notes` variant's notes field (#806), with what is stored. */
  notes?: { stored: string };
  onSubmit: (values: { name: string; communityId: string; notes: string }) => void;
}

export function CommunitySpecFormDialog({
  open,
  onOpenChange,
  pending,
  failed,
  onErrorClose,
  onSubmit,
  onCloseAutoFocus,
  copy,
  submitCopy,
  storedName,
  storedCommunityId: storedId,
  initialName = storedName,
  initialCommunityId = storedId,
  parent,
  division,
  notes: notesField,
}: CommunitySpecFormDialogProps) {
  const [name, setName] = useState(initialName);
  const [notes, setNotes] = useState(notesField?.stored ?? "");
  const [community, setCommunity] = useState<Pick<
    CommunityIdChange,
    "compositeId" | "isValid" | "ruleMessage"
  > | null>(null);
  const [submitted, setSubmitted] = useState(false);
  // react-hook-form's `isSubmitting`, set by this opening's own send: the
  // island resets the form on every open, so a dialog opened again over a
  // rename still in flight has a live name under a spinning Save
  // (`CommunitySpecCreateEditDupIsland/data/observers.ts:78-84`).
  const [sent, setSent] = useState(false);
  const [communityError, setCommunityError] = useState<string | undefined>(undefined);

  const compositeId = community?.compositeId ?? initialCommunityId;
  // The donor's rule is the name and the id; the notes variant adds the notes
  // (D2b, owner-ruled — the source could not save a notes-only change).
  const blocked =
    name === storedName &&
    compositeId === storedId &&
    (notesField === undefined || notes === notesField.stored);

  const submit = () => {
    if (blocked || pending) return;
    setSubmitted(true);
    const fieldRule =
      community && !community.isValid ? (community.ruleMessage ?? undefined) : undefined;
    setCommunityError(fieldRule);
    if (!name || fieldRule !== undefined) return;
    setSent(true);
    onSubmit({ name, communityId: compositeId, notes });
  };

  return (
    <FormDialogFrame
      open={open}
      onOpenChange={onOpenChange}
      copy={copy}
      submitCopy={submitCopy}
      formId={COMMUNITY_FORM_ID}
      pending={pending}
      blocked={blocked}
      failed={failed}
      onErrorClose={onErrorClose}
      onCloseAutoFocus={onCloseAutoFocus}
    >
      {/* `VStack component='form' spacing={2} alignItems='flex-start'`. */}
      <form
        id={COMMUNITY_FORM_ID}
        noValidate
        autoComplete="off"
        className="flex flex-col items-start gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <BasedOnLine parent={parent} />
        <TextField
          label={COPY.nameLabel}
          required
          value={name}
          onChange={setName}
          error={submitted && !name ? COPY.requiredField : undefined}
          disabled={sent && pending}
          className="w-full"
        />
        {division}
        <CommunityIdAutocomplete
          initialCompositeId={initialCommunityId}
          onChange={({ compositeId: next, isValid, ruleMessage }) =>
            setCommunity({ compositeId: next, isValid, ruleMessage })
          }
          error={communityError}
        />
        {notesField ? (
          // `RichTextField fullWidth … sx={{ maxWidth: 'calc(100% - 2px)' }}`.
          <RichTextField
            label={COPY.header.notesLabel}
            placeholder={COPY.header.notesPlaceholder}
            value={notes}
            onChange={setNotes}
            disabled={sent && pending}
            className="w-full max-w-[calc(100%-2px)]"
          />
        ) : null}
      </form>
    </FormDialogFrame>
  );
}

/**
 * "Based on: <name> (v.<code>)" — the community EDIT variant's own line, not
 * the create dialog's: `h5` both halves, the label in `text.secondary`, one
 * space before the `(v.`, and a code the version lacks printed as nothing
 * (`versionCode || ''`). The link opens the base version's page in a new tab,
 * primary and not underlined.
 *
 * **Always drawn**, as the source's EDIT branch is — it has no conditional.
 * A row with no base (the one carried community spec without one) has nothing
 * to link to: the source's text there is ` (v.)` over a link to
 * `/specifications/undefined/…`, so the text stands and the link does not.
 */
export function BasedOnLine({ parent }: { parent: SpecParentLink }) {
  const text = `${parent?.name ?? ""} (v.${parent?.versionCode ?? ""})`;
  return (
    // `HStack spacing={1} alignItems='baseline'`.
    <div className="flex items-baseline gap-2">
      <MuiTypography variant="h5" color="secondary" className="shrink-0">
        {COPY.rename.basedOn}
      </MuiTypography>
      <MuiLink
        underline="none"
        {...(parent
          ? {
              href: specDetailPath(parent.specificationId, parent.versionId),
              target: "_blank",
            }
          : {})}
      >
        {/* An `h5` inside the link, as the donor's `Typography variant='h5'`
            is — the Communities field's name line keeps the same element. */}
        <MuiTypography variant="h5">{text}</MuiTypography>
      </MuiLink>
    </div>
  );
}

interface FormDialogFrameProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  copy: { title: string; subtitle: string; error: string };
  submitCopy: { label: string; pending: string; ariaLabel: string };
  formId: string;
  pending: boolean;
  blocked: boolean;
  failed: boolean;
  onErrorClose: () => void;
  onCloseAutoFocus?: (event: Event) => void;
  children: React.ReactNode;
}

/**
 * The shell every name dialog here shares — the two rename donors and the
 * community island's create and duplicate: `sm`, Cancel then submit, and the
 * error snackbar.
 */
function FormDialogFrame({
  open,
  onOpenChange,
  copy,
  submitCopy,
  formId,
  pending,
  blocked,
  failed,
  onErrorClose,
  onCloseAutoFocus,
  children,
}: FormDialogFrameProps) {
  return (
    <>
      <AppDialog
        open={open}
        onOpenChange={onOpenChange}
        title={copy.title}
        subtitle={copy.subtitle}
        size="sm"
        onOpenAutoFocus={focusPaperThenFirstInput}
        onCloseAutoFocus={onCloseAutoFocus}
        actions={
          <AppDialogActions>
            <DialogSecondaryButton onClick={() => onOpenChange(false)}>
              {COPY.cancel}
            </DialogSecondaryButton>
            <DialogPrimaryButton
              type="submit"
              form={formId}
              aria-label={submitCopy.ariaLabel}
              loading={pending}
              disabled={blocked}
            >
              {pending ? submitCopy.pending : submitCopy.label}
            </DialogPrimaryButton>
          </AppDialogActions>
        }
      >
        {children}
        {/* Inside the dialog, as the donor renders it: it lives through the
            dialog's exit and goes when the dialog unmounts. It portals to the
            body either way. */}
        <ErrorSnackbar open={failed} message={copy.error} onClose={onErrorClose} />
      </AppDialog>
    </>
  );
}
